import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Package, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2,
  X,
  Check,
  Image as ImageIcon,
  Tag,
  IndianRupee,
  Box
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';

const ProductListScreen = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    mrp: '',
    discount: '',
    offer: '',
    description: '',
    image: '',
    brand: '',
    category: '',
    subcategory: '',
    countInStock: '',
    isFeatured: false,
    tags: ''
  });

  const { getConfig } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/products`),
        axios.get(`${API_URL}/categories?showAll=true`)
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        price: product.price,
        mrp: product.mrp || '',
        discount: product.discount || (product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : ''),
        offer: product.offer || '',
        description: product.description,
        image: product.image,
        brand: product.brand,
        category: product.category?._id || product.category,
        subcategory: product.subcategory?._id || product.subcategory || '',
        countInStock: product.countInStock,
        isFeatured: product.isFeatured,
        tags: product.tags?.join(', ') || ''
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '', price: '', mrp: '', discount: '', offer: '', description: '', image: '',
        brand: '', category: '', subcategory: '', countInStock: '',
        isFeatured: false, tags: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t !== ''),
        subcategory: formData.subcategory || null
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct._id}`, payload, getConfig());
      } else {
        await axios.post(`${API_URL}/products`, payload, getConfig());
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving product');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this product?')) {
      try {
        await axios.delete(`${API_URL}/products/${id}`, getConfig());
        fetchData();
      } catch (error) {
        alert('Error deleting product');
      }
    }
  };

  const selectedCategory = categories.find(c => c._id === formData.category);
  const subcategories = selectedCategory?.subcategories || [];

  return (
    <div className="p-8 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Inventory</h1>
          <p className="text-secondary-500">Manage your product catalog and stock levels.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-primary-200 flex items-center space-x-2 transition-all"
        >
          <Plus className="h-5 w-5" />
          <span>Add New Product</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="bg-white p-4 rounded-3xl border border-secondary-100 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-4 top-3 text-secondary-400 h-5 w-5" />
          <input 
            type="text" 
            placeholder="Search by name, category, or brand..." 
            className="w-full bg-secondary-50 border-none rounded-2xl py-3 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none font-bold"
          />
        </div>
        <div className="flex items-center space-x-4 w-full md:w-auto">
          <button className="flex-1 md:flex-none flex items-center justify-center space-x-2 px-4 py-3 bg-white border border-secondary-200 rounded-2xl font-bold text-secondary-600 hover:bg-secondary-50 transition-all">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </button>
          <div className="h-8 w-px bg-secondary-100 hidden md:block" />
          <p className="text-sm font-bold text-secondary-500 hidden md:block">
            Showing <span className="text-secondary-900">{products.length}</span> products
          </p>
        </div>
      </div>

      {/* Product Table */}
      <div className="bg-white rounded-[32px] border border-secondary-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary-50/50 text-left border-b border-secondary-100">
                <th className="px-8 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Product</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Category</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Price</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Stock</th>
                <th className="px-6 py-5 font-black text-secondary-400 text-[10px] uppercase tracking-widest">Status</th>
                <th className="px-8 py-5 text-right font-black text-secondary-400 text-[10px] uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-50">
              {loading ? (
                [1, 2, 3].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="6" className="px-8 py-6 h-20 bg-secondary-50/20" />
                  </tr>
                ))
              ) : (
                products.map((product) => (
                  <tr key={product._id} className="hover:bg-secondary-50/50 transition-colors group">
                    <td className="px-8 py-5">
                      <div className="flex items-center space-x-4">
                        <img src={product.image} alt={product.name} className="w-12 h-12 rounded-xl object-cover shadow-sm" />
                        <div>
                          <p className="font-bold text-secondary-900 group-hover:text-primary-600 transition-colors line-clamp-1">{product.name}</p>
                          <p className="text-[10px] text-secondary-400 font-bold uppercase tracking-wider">{product.brand}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-secondary-900 italic">
                          {product.category?.name || 'N/A'}
                        </span>
                        {product.subcategory && (
                          <span className="text-[10px] font-bold text-secondary-400">
                            → {product.subcategory?.name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="font-black text-secondary-900 italic">₹{product.price}</span>
                        {product.mrp > product.price && (
                          <span className="text-[10px] text-secondary-400 line-through">₹{product.mrp}</span>
                        )}
                        {(product.offer || product.discount > 0 || (product.mrp > product.price)) && (
                          <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded-md text-[9px] font-black bg-emerald-100 text-emerald-700 w-max">
                            {product.offer || `${product.discount || Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF`}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center space-x-2">
                        <div className={`h-2 w-2 rounded-full ${product.countInStock > 10 ? 'bg-emerald-500' : 'bg-orange-500'}`} />
                        <span className="font-bold text-secondary-700">{product.countInStock}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        product.countInStock > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {product.countInStock > 0 ? 'In Stock' : 'Out of Stock'}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          onClick={() => handleOpenModal(product)}
                          className="p-2 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                        >
                          <Edit3 className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDelete(product._id)}
                          className="p-2 text-secondary-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && products.length === 0 && (
          <div className="p-20 text-center space-y-4">
            <Package className="h-16 w-16 text-secondary-200 mx-auto" />
            <p className="text-secondary-500 font-bold">No products found in your inventory.</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-[40px] w-full max-w-4xl my-8 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 md:p-12 space-y-8">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-black italic text-secondary-900 uppercase tracking-tighter">
                    {editingProduct ? 'Update Product' : 'Add New Product'}
                  </h2>
                  <p className="text-secondary-500 font-medium">Fill in the details below to {editingProduct ? 'update' : 'create'} your product.</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-3 hover:bg-secondary-100 rounded-2xl transition-all">
                  <X className="h-6 w-6 text-secondary-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column: Basic Info */}
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Product Name</label>
                    <div className="relative">
                      <Tag className="absolute left-4 top-4 h-5 w-5 text-secondary-400" />
                      <input 
                        required
                        type="text" 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-secondary-50 border-none rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                        placeholder="e.g. Premium Silk Saree"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">MRP (₹)</label>
                      <div className="relative">
                        <IndianRupee className="absolute left-4 top-4 h-5 w-5 text-secondary-400" />
                        <input 
                          type="number" 
                          value={formData.mrp}
                          onChange={(e) => {
                            const newMrp = e.target.value;
                            let newPrice = formData.price;
                            if (newMrp && formData.discount) {
                              newPrice = Math.round(Number(newMrp) * (1 - Number(formData.discount) / 100));
                            }
                            setFormData({ ...formData, mrp: newMrp, price: newPrice });
                          }}
                          className="w-full bg-secondary-50 border-none rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                          placeholder="1499"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Selling Price (₹)</label>
                      <div className="relative">
                        <IndianRupee className="absolute left-4 top-4 h-5 w-5 text-secondary-400" />
                        <input 
                          required
                          type="number" 
                          value={formData.price}
                          onChange={(e) => {
                            const newPrice = e.target.value;
                            let newDiscount = formData.discount;
                            if (formData.mrp && Number(formData.mrp) > Number(newPrice)) {
                              newDiscount = Math.round(((Number(formData.mrp) - Number(newPrice)) / Number(formData.mrp)) * 100);
                            }
                            setFormData({ ...formData, price: newPrice, discount: newDiscount });
                          }}
                          className="w-full bg-secondary-50 border-none rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                          placeholder="999"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Offer / Discount Box */}
                  <div className="grid grid-cols-2 gap-4 bg-emerald-50/60 p-4 rounded-3xl border border-emerald-100">
                    <div className="space-y-2">
                      <label className="text-xs font-black text-emerald-800 uppercase tracking-widest ml-1">Offer / Discount (%)</label>
                      <div className="relative">
                        <span className="absolute right-4 top-3.5 text-xs font-black text-emerald-600">% OFF</span>
                        <input 
                          type="number" 
                          min="0"
                          max="99"
                          value={formData.discount}
                          onChange={(e) => {
                            const newDisc = e.target.value;
                            let newPrice = formData.price;
                            if (formData.mrp && newDisc !== '') {
                              newPrice = Math.round(Number(formData.mrp) * (1 - Number(newDisc) / 100));
                            }
                            setFormData({ ...formData, discount: newDisc, price: newPrice });
                          }}
                          className="w-full bg-white border border-emerald-200 rounded-2xl py-3 pl-4 pr-16 focus:ring-2 focus:ring-emerald-500 outline-none font-bold text-secondary-900"
                          placeholder="e.g. 20"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-emerald-800 uppercase tracking-widest ml-1">Offer Badge (Optional)</label>
                      <input 
                        type="text" 
                        value={formData.offer}
                        onChange={(e) => setFormData({ ...formData, offer: e.target.value })}
                        className="w-full bg-white border border-emerald-200 rounded-2xl py-3 px-4 focus:ring-2 focus:ring-emerald-500 outline-none font-bold text-secondary-900 text-xs"
                        placeholder="e.g. Special Deal"
                      />
                    </div>
                    {formData.mrp && formData.price && Number(formData.mrp) > Number(formData.price) && (
                      <div className="col-span-2 text-xs font-bold text-emerald-700 flex items-center justify-between pt-1">
                        <span>Customer Saves: ₹{Number(formData.mrp) - Number(formData.price)}</span>
                        <span className="bg-emerald-600 text-white px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase">
                          {formData.offer || `${formData.discount || Math.round(((Number(formData.mrp) - Number(formData.price)) / Number(formData.mrp)) * 100)}% OFF`}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Description</label>
                    <textarea 
                      required
                      rows="4"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-3xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-medium text-secondary-900"
                      placeholder="Detailed product description..."
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Product Image</label>
                    <div className="flex flex-col space-y-4">
                      <div className="relative">
                        <ImageIcon className="absolute left-4 top-4 h-5 w-5 text-secondary-400" />
                        <input 
                          required
                          type="text" 
                          value={formData.image}
                          onChange={(e) => setFormData({...formData, image: e.target.value})}
                          className="w-full bg-secondary-50 border-none rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                          placeholder="Paste image URL here..."
                        />
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div className="h-px bg-secondary-100 flex-1" />
                        <span className="text-[10px] font-black text-secondary-300 uppercase tracking-widest">OR</span>
                        <div className="h-px bg-secondary-100 flex-1" />
                      </div>

                      <div className="relative">
                        <input 
                          type="file" 
                          id="productImageUpload"
                          className="hidden" 
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            const uploadFormData = new FormData();
                            uploadFormData.append('image', file);
                            setIsSubmitting(true);
                            try {
                              const { data } = await axios.post(`${API_URL}/upload`, uploadFormData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              setFormData({ ...formData, image: data.image });
                            } catch (error) {
                              alert('Upload failed');
                            } finally {
                              setIsSubmitting(false);
                            }
                          }}
                        />
                        <label 
                          htmlFor="productImageUpload"
                          className="w-full flex items-center justify-center space-x-2 bg-secondary-900 text-white py-4 rounded-2xl font-black italic uppercase cursor-pointer hover:bg-secondary-800 transition-all shadow-xl shadow-secondary-200"
                        >
                          <Plus className="h-5 w-5" />
                          <span>Upload from Computer</span>
                        </label>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Right Column: Categorization & Stock */}
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Brand</label>
                      <input 
                        required
                        type="text" 
                        value={formData.brand}
                        onChange={(e) => setFormData({...formData, brand: e.target.value})}
                        className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                        placeholder="Brand Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Stock</label>
                      <div className="relative">
                        <Box className="absolute left-4 top-4 h-5 w-5 text-secondary-400" />
                        <input 
                          required
                          type="number" 
                          value={formData.countInStock}
                          onChange={(e) => setFormData({...formData, countInStock: e.target.value})}
                          className="w-full bg-secondary-50 border-none rounded-2xl py-4 pl-12 pr-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                          placeholder="50"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Category</label>
                    <select 
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value, subcategory: ''})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 appearance-none"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Subcategory (Optional)</label>
                    <select 
                      value={formData.subcategory}
                      onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900 appearance-none"
                      disabled={!formData.category}
                    >
                      <option value="">No Subcategory</option>
                      {subcategories.map(sub => (
                        <option key={sub._id} value={sub._id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-black text-secondary-400 uppercase tracking-widest ml-1">Tags (Comma Separated)</label>
                    <input 
                      type="text" 
                      value={formData.tags}
                      onChange={(e) => setFormData({...formData, tags: e.target.value})}
                      className="w-full bg-secondary-50 border-none rounded-2xl py-4 px-6 focus:ring-2 focus:ring-primary-500 outline-none font-bold text-secondary-900"
                      placeholder="silk, saree, ethnic, green"
                    />
                  </div>

                  <div className="flex items-center p-4 bg-secondary-50 rounded-2xl">
                    <input 
                      type="checkbox" 
                      id="isFeatured"
                      checked={formData.isFeatured}
                      onChange={(e) => setFormData({...formData, isFeatured: e.target.checked})}
                      className="h-6 w-6 rounded-lg text-primary-600 focus:ring-primary-500 border-none bg-white"
                    />
                    <label htmlFor="isFeatured" className="ml-3 font-bold text-secondary-700">Feature this product on homepage</label>
                  </div>

                  <div className="pt-4 flex space-x-4">
                    <button 
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-8 py-5 rounded-3xl font-bold text-secondary-500 hover:bg-secondary-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      disabled={isSubmitting}
                      className="flex-[2] bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 text-white px-8 py-5 rounded-3xl font-black italic uppercase shadow-xl shadow-primary-200 transition-all flex items-center justify-center space-x-2"
                    >
                      {isSubmitting ? (
                         <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Check className="h-6 w-6" />
                          <span>{editingProduct ? 'Update Product' : 'Create Product'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductListScreen;

